import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Stack,
  Tab,
  Typography,
  Tooltip,
  IconButton,
  TableCell,
} from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import {
  Sick,
  InfoOutlined,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { GenericTable } from "@/components/GenericTable/GenericTable";
import { useDatetime } from "@/utils/useDatetime";
import { useCallback, useState } from "react";
import { FollowUpModal } from "../../../shared/FollowUpModal";
import { FollowUpActions } from "../FollowUpActions/FollowUpActions";
import { PatchEHRPoster } from "@/services/fhir/patientsummary/PatchEHRPoster";
import { JustificationModal } from "@/pages/ehrrecords/components/patientSummaryAccordions/Prescriptions/JustificationModal";
import { usePostAttendanceFollowUpSendDialog } from "../../../../hooks/usePostAttendanceFollowUpSendDialog";
import { SendWhatsAppMessageDialog } from "src-next/modules/crm-clinical/messaging";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import {
  filterEntriesByOrganization,
  getFollowUpOrganizationFilterMessage,
} from "../../utils/postAttendanceHelpers";

interface MedicationsProps {
  data: any;
  documentReference: any;
  onUpdate: () => void;
  patientData?: { id: string; name: string; phone?: string };
}

export function Medications({
  data,
  documentReference,
  onUpdate,
  patientData,
}: MedicationsProps) {
  const { organizationId, organization } = useOrganizationContext();
  const { formatToDDMMYYYY } = useDatetime;
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState("draft");
  const [sendingFollowUp, setSendingFollowUp] = useState(false);
  const [openJustification, setOpenJustification] = useState(false);
  const [selectedJustification, setSelectedJustification] = useState<{
    note?: string;
    status?: string;
  }>({});

  const handleFollowUpSent = useCallback(
    async ({ row }: { row: any }) => {
      const notificationSent = row?.medicationRequest?.extension?.find(
        (extension: any) => extension?.url.includes("notificationSent"),
      );

      if (!notificationSent && row?.medicationRequest?.id) {
        await PatchEHRPoster({
          resourceType: "MedicationRequest",
          resourceId: row.medicationRequest.id,
          operation: "add",
          path: "/extension",
          value: [
            {
              url: "https://fluxmed.com.br/fhir/StructureDefinition/notificationSent",
              valueString: true,
            },
          ],
        });
      }

      onUpdate();
    },
    [onUpdate],
  );

  const { sendFollowUp, sendDialogProps } = usePostAttendanceFollowUpSendDialog({
    onFollowUpSent: handleFollowUpSent,
  });

  const handleOpenModal = (item: any) => {
    setSelectedItem(item);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedItem(null);
  };

  const handleSendFollowUp = async () => {
    if (!selectedItem) return;

    setSendingFollowUp(true);
    try {
      const sent = await sendFollowUp({
        row: selectedItem,
        type: "medication",
        patientData,
      });

      if (sent) {
        handleCloseModal();
      }
    } finally {
      setSendingFollowUp(false);
    }
  };

  const handleOpenJustification = (note?: string, status?: string) => {
    setSelectedJustification({ note, status });
    setOpenJustification(true);
  };

  const handleCloseJustification = () => {
    setOpenJustification(false);
    setSelectedJustification({});
  };

  const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const formatPrescriptionData = (filteredData: any[]) => {
    return filteredData
      ?.map((el: any) => {
        const docRef = documentReference?.find((doc: any) => {
          const findRef = doc?.resource?.basedOn?.find((basedOn: any) =>
            basedOn?.reference.includes(el.resource.id)
          );
          return findRef;
        });

        const informationSource = el?.resource?.informationSource?.find(
          (source: any) => source?.reference?.includes("Practitioner")
        )?.display;
        const auxPractitioner = docRef?.resource?.contained?.find(
          (contained: any) => contained?.resourceType === "Practitioner"
        );

        const auxSpecialty =
          docRef?.resource?.practiceSetting?.coding?.[0]?.display;

        const status = el?.resource?.status;

        const description = el.resource?.medication?.concept?.text || "";
        const date = el?.resource?.effectiveDosePeriod?.start
          ? formatToDDMMYYYY(dayjs(el?.resource?.effectiveDosePeriod?.start))
          : "-";
        const binaryUrl = docRef?.resource?.content?.[0]?.attachment?.url || "";

        const dosage = el?.resource?.dosageInstruction?.[0];
        const auxType = dosage?.doseAndRate?.[0]?.type?.text;
        const auxQtd = dosage?.doseAndRate?.[0]?.doseQuantity;
        const auxUnit = auxQtd?.unit;
        const auxUnitValue = auxQtd?.value;

        const auxTiming = dosage?.timing?.repeat;
        const auxDuration = auxTiming?.duration;
        const auxPeriod = auxTiming?.period;

        const sign =
          el?.resource?.securityLabel?.[0]?.coding?.[0]?.code ===
          "ProofOfOrigin";

        return {
          encounterId: el?.resource?.encounter?.reference?.split("/")?.[1],
          status,
          description,
          date,
          sign,
          dose: `${
            auxQtd?.unit && auxQtd?.value && auxType
              ? `${auxUnitValue && `${auxUnitValue}`} ${
                  auxUnit && `${auxUnit}`
                }`
              : ""
          } ${
            auxPeriod
              ? `${auxPeriod && `a cada ${auxPeriod} h`} ${
                  auxDuration
                    ? `durante ${auxDuration} dia(s)`
                    : "por tempo indeterminado"
                }`
              : ""
          }`,
          specialty: auxSpecialty || "-",
          binaryUrl,
          requesterId: el?.resource?.requester?.reference,
          practitioner:
            informationSource ||
            auxPractitioner?.name?.[0]?.given?.[0] ||
            auxPractitioner?.identifier?.[0]?.value ||
            el?.resource?.requester?.reference?.display ||
            el?.resource?.requester?.reference?.split("-")?.[2],
          medicationRequest: el?.resource,
        };
      })
      ?.sort((a: any, b: any) => {
        const dateA = dayjs(a.date, "DD/MM/YYYY");
        const dateB = dayjs(b.date, "DD/MM/YYYY");
        return dateB.diff(dateA);
      });
  };

  const columns = [
    {
      column: "Medicamentos",
      rowName: "description",
    },
    { column: "Posologia", rowName: "dose" },
    { column: "Profissional", rowName: "practitioner" },
    { column: "Data", rowName: "date" },
  ];

  const draftItems = data?.filter((el: any) => el?.resource?.status === "draft");

  // Aba Solicitados: exibe apenas registros da clínica logada (acompanhamento via WhatsApp)
  const draftItemsFromOrganization = filterEntriesByOrganization(
    draftItems,
    organizationId,
    (el) => el?.resource?.requester?.reference,
  );

  const statusGroups = {
    draft: draftItemsFromOrganization,
    active: data?.filter((el: any) => el?.resource?.status === "active"),
    stopped: data?.filter((el: any) => el?.resource?.status === "stopped"),
    completed: data?.filter((el: any) => el?.resource?.status === "completed"),
  };

  const getStatusCount = (status: keyof typeof statusGroups) => {
    return statusGroups[status]?.length || 0;
  };

  const renderTableActions = (status: string, row: any) => {
    if (status === "draft") {
      return <FollowUpActions row={row} onOpenModal={handleOpenModal} />;
    }

    if (
      (status === "stopped" || status === "completed") &&
      // Busca especificamente a nota do paciente (com authorReference que contém 'Patient')
      // em vez de sempre pegar a primeira nota do array
      row?.medicationRequest?.note?.find((noteItem: any) => 
        noteItem?.authorReference?.reference?.includes('Patient')
      )?.text
    ) {
      return (
        <TableCell align="center">
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={1}
          >
            <Tooltip
              title={
                status === "stopped"
                  ? "Ver motivo da interrupção"
                  : "Ver observação do paciente"
              }
            >
              <IconButton
                onClick={() =>
                  handleOpenJustification(
                    // Busca a nota do paciente para exibir no modal de justificativa
                    row.medicationRequest.note.find((noteItem: any) => 
                      noteItem?.authorReference?.reference?.includes('Patient')
                    )?.text,
                    status
                  )
                }
                size="small"
                color="primary"
              >
                {status === "stopped" ? <Sick /> : <InfoOutlined />}
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>
      );
    }

    return null;
  };

  const renderStatusMessage = (status: string) => {
    switch (status) {
      case "draft":
        return (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              backgroundColor: "info.lighter",
              p: 1.5,
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Typography variant="body1" color="text.secondary">
              {getFollowUpOrganizationFilterMessage(organization?.name)}
            </Typography>
          </Stack>
        );
      case "active":
        return (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Medicamentos atualmente em uso pelo paciente.
          </Typography>
        );
      case "stopped":
        return (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Medicamentos que foram interrompidos antes do término previsto.
          </Typography>
        );
      case "completed":
        return (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Medicamentos que foram utilizados até o fim do tratamento.
          </Typography>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Accordion sx={{ width: "100%" }} defaultExpanded elevation={3}>
        <AccordionSummary
          expandIcon={<GridExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Stack direction="row" alignItems="center" gap={1}>
            Medicamentos
            <Avatar
              sx={{
                width: 26,
                height: 26,
                fontSize: "14px",
                fontWeight: 600,
                backgroundColor: "primary100.main",
                color: "primary1000.main",
              }}
            >
              {data?.length || "0"}
            </Avatar>
          </Stack>
        </AccordionSummary>

        <AccordionDetails>
          <TabContext value={selectedTab}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <TabList onChange={handleChangeTab} variant="scrollable">
                <Tab
                  label={`Solicitados (${getStatusCount("draft")})`}
                  value="draft"
                />
                <Tab
                  label={`Em uso (${getStatusCount("active")})`}
                  value="active"
                />
                <Tab
                  label={`Interrompidos (${getStatusCount("stopped")})`}
                  value="stopped"
                />
                <Tab
                  label={`Finalizados (${getStatusCount("completed")})`}
                  value="completed"
                />
              </TabList>
            </Box>

            {Object.entries(statusGroups).map(([status, items]) => (
              <TabPanel key={status} value={status} sx={{ p: 2 }}>
                {renderStatusMessage(status)}
                <Box sx={{ maxHeight: "60vh", overflow: "auto" }}>
                  <GenericTable
                    tableColumns={columns}
                    tableRows={formatPrescriptionData(items)}
                    actions={
                      status === "draft" ||
                      status === "stopped" ||
                      status === "completed"
                        ? (row: any) => renderTableActions(status, row)
                        : undefined
                    }
                  />
                </Box>
              </TabPanel>
            ))}
          </TabContext>
        </AccordionDetails>
      </Accordion>

      <FollowUpModal
        open={openModal}
        onClose={handleCloseModal}
        title="Acompanhamento de Medicamento"
        data={selectedItem}
        type="medication"
        onSendFollowUp={handleSendFollowUp}
        loading={sendingFollowUp}
        patientData={patientData}
      />

      {sendDialogProps?.open ? (
        <SendWhatsAppMessageDialog {...sendDialogProps} />
      ) : null}

      <JustificationModal
        open={openJustification}
        onClose={handleCloseJustification}
        note={selectedJustification.note}
        status={selectedJustification.status}
      />
    </>
  );
}
